/**
 * Map ``octopus_loa_fields`` form data → ``LoaTemplatePdfConfig``.
 *
 * The 4 LOA React templates accept a uniform ``config.config`` shape
 * (legacy report-builder ``ReportConfig``). This adapter takes the
 * agentic-v2 form values written by the LOA drafting tool and produces
 * exactly that shape — so the templates can render against the
 * artefact's form data with zero template changes.
 */

import type { LoaTemplatePdfConfig } from "./index";
import { formDataToEditValues } from "@/components/artifacts/formDataUtils";

type FormValues = Record<string, unknown>;

function str(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "string") return v;
  return String(v);
}

/** Split a comma-separated single-line address into an array of lines. */
function splitAddress(raw: string): string[] {
  if (!raw) return [];
  return raw
    .split(/[,\n]/)
    .map((part) => part.trim())
    .filter(Boolean);
}

export function loaFormDataToPdfConfig(
  formData: FormValues,
): LoaTemplatePdfConfig {
  const flat = formDataToEditValues(formData);
  const clientAddress = splitAddress(str(flat.client_address));
  const providerAddress = splitAddress(str(flat.provider_address));

  return {
    config: {
      client: {
        name: str(flat.client_full_name),
        dob: str(flat.client_date_of_birth),
        ni: str(flat.client_ni_number),
        address: clientAddress,
      },
      provider: {
        name: str(flat.provider_name),
        address: providerAddress,
      },
      policies: [
        {
          type: str(flat.plan_type),
          number: str(flat.policy_number),
        },
      ],
      adviser: { name: str(flat.adviser_name) || "The Octopus Money Team" },
      company: {
        email: str(flat.company_email) || "team@octopusmoney.com",
        fax: "",
        phone: str(flat.company_phone) || "020 3650 2050",
        address: splitAddress(
          str(flat.company_post) ||
            "Octopus Money, 7th Floor, 33 Holborn, London, EC1N 2HT, UNITED KINGDOM",
        ),
        regulation:
          "Octopus Money is authorised and regulated by the Financial Conduct Authority.",
        registration: "FCA registration number: 763630",
        fcaNumber: str(flat.company_fca) || "763630",
      },
      asAt: str(flat.date) || new Date().toISOString().split("T")[0],
    },
  };
}
